<template>
  <VcModalContainer
    :size="isMobile ? 'responsive' : 'md'"
    :show-dialog="showDialog"
    :flavor="isMobile ? 'input' : ''"
    showHeaderDivider>
    <template #header>
      <VcLayout align-center justify-space-between class="pa-4">
        <VcLayout align-center>
          <VcButton icon class="mr-3" @click="handleBack">
            <template #prepend>
              <VcIcon size="16">icon-Chevron-left</VcIcon>
            </template>
          </VcButton>
          <div class="mobile-custom-instructions-title">
            {{ headerText }}
          </div>
        </VcLayout>
        <VcButton icon @click="handleClose">
          <template #prepend>
            <VcIcon size="16">icon-close_new</VcIcon>
          </template>
        </VcButton>
      </VcLayout>
    </template>
    <template #content>
      <VcLayout column class="mobile-custom-instructions-wrapper">
        <VcLayout v-if="isGenerating" class="ai-cover" align-start>
          <VcLayout align-center>
            <span class="ai-cover--icon ml-4">
              <StarAi />
            </span>
            <span class="ml-4">{{ $t('ai_content_generator.custom_instructions.generating_text') }}</span>
            <VcLink :label="$t('ai_content_generator.custom_instructions.cancel_btn')"
                    @click="$emit('cancelAiGeneration')"
                    class="ai-cover--link ml-4"
            />
          </VcLayout>
        </VcLayout>
        <VcLayout v-else class="mobile-custom-instructions-preview pa-4">
          <VcLayout justify-space-between column class="mobile-custom-instructions-preview-content">
            <div class="mobile-custom-instructions-preview-text pb-14" v-html="currentMessage"></div>
          </VcLayout>
        </VcLayout>
        <VcLayout class="mobile-custom-instructions-input-container pa-4" column>
          <div v-if="generatedReply && generatedReply.trim()" class="mobile-custom-instructions-generated">
            <VcAiButton
              pill
              small
              secondary
              use-ai-icon
              secondary-variant="gradient"
              :label="insertReplyLabel"
              @click="onInsertReply"
              dataQa="insert-smart-reply-mobile"
              class="ai-btn"
            />
          </div>
          <VcLayout align-center>
            <VcTextArea
              v-model="instructionsText"
              placeholder="Give instructions, like 'Keep it casual'"
              class="mobile-custom-instructions-field"
              :maxlength="1000"
              @keyup.enter="generate"
              noResize
              autoGrow
              ref="instructionsText"
            />
            <VcButton
              icon
              small
              @click="generate"
              class="mobile-custom-instructions-send-btn ml-2"
              :disabled="isGenerating || !instructionsText || instructionsText.trim().length < 2"
            >
              <VcIcon>icon-arrow-up</VcIcon>
            </VcButton>
          </VcLayout>
        </VcLayout>
      </VcLayout>
    </template>
  </VcModalContainer>
</template>

<script>
import VcAiButton from '@vcita/design-system/src/components/VcAiButton/VcAiButton.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'MobileCustomInstructions',
  components: {
    VcAiButton,
    StarAi: () => import('@/modules/bizai/assets/starAi.svg'),
  },
  props: {
    showDialog: {
      type: Boolean,
      default: true,
    },
    currentMessage: {
      type: String,
      default: '',
    },
    generatedReply: {
      type: String,
      default: '',
    },
    insertReplyLabel: {
      type: String,
      default: 'Insert reply',
    },
    initialInstructions: {
      type: String,
      default: '',
    },
    headerText: {
      type: String,
      default: 'Give instructions',
    },
  },
  computed: {
    ...mapGetters('CommonStore', ['isMobile']),
    ...mapGetters('AiGeneratorStore', ['isGenerating']),
  },
  data() {
    return {
      instructionsText: this.initialInstructions || '',
    };
  },
  watch: {
    initialInstructions(newVal) {
      if (newVal) {
        this.instructionsText = newVal;
      }
    },
  },
  methods: {
    handleClose() {
      this.instructionsText = '';
      this.$emit('close');
    },
    handleBack() {
      this.$emit('back');
    },
    generate() {
      const userInstructions = this.instructionsText.trim();
      // Don't clear the input or close - keep the view open
      this.$emit('generate', userInstructions);
    },
    onInsertReply() {
      this.$emit('insertReply');
    },
    focusOnInstructionsInput() {
      if (!this.$refs.instructionsText) return;

      setTimeout(() => {
        const textareaElement = this.$refs.instructionsText.$el?.querySelector('textarea');
        if (textareaElement) {
          textareaElement.focus();
        }
      }, 0);
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes star-rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.mobile-custom-instructions-title {
  font-weight: var(--font-weight-large);
  font-size: var(--font-size-small2);
  padding-inline-end: var(--size-value6);
}
.mobile-custom-instructions-wrapper {
  height: 100%;
}
.mobile-custom-instructions-preview {
  position: relative;
  background-color: var(--gray-lighten-4);
  border-bottom: 1px solid var(--gray-lighten-3);
  height: 100%;
  overflow: auto;
  max-height: calc(100vh - 68px);

  .mobile-custom-instructions-preview-content {
    gap: 8px;
  }

  .mobile-custom-instructions-preview-text {
    font-size: var(--font-size-small);
  }
}

.mobile-custom-instructions-generated {
  position: absolute;
  left: var(--size-value4);
  top: -48px;
}

.mobile-custom-instructions-send-btn {
  ::v-deep .v-icon {
    color: white !important;
  }

  &:not(:disabled) {
    background-color: var(--v-secondary-base);
  }
}

.mobile-custom-instructions-field {
  ::v-deep {
    textarea {
      //max 3 lines
      max-height: 70px;
      overflow: auto;
    }
    &.VcTextArea.v-text-field .v-text-field__slot:first-child {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
}

.mobile-custom-instructions-input-container {
  position: relative;
}

.ai-cover {
  height: 100%;
  gap: var(--size-value4);
  padding: var(--size-value4);
  background-color: var(--gray-lighten-4);

  &--icon {
    width: var(--size-value4);
    height: var(--size-value4);
    animation: star-rotate 2.5s linear infinite;
  }
  .ai-cover--link {
    padding-top: 0;
  }
}
</style>
